import React, { Component, ReactNode } from 'react';
import { DataStructure } from '../../domain/structure/DataStructureDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import LanguageUtil from '../../util/LanguageUtil';
import StructService from '../../service/StructService';
import ServiceContainer from 'config/ServiceContainer';
import { ServiceNames } from 'constant/ServiceNames';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import { Tooltip } from 'primereact/tooltip';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;

class StructListViewerComponent extends Component<Props> {
  private structService: StructService = ServiceContainer.getInstance().get(ServiceNames.STRUCT);

  public render(): ReactNode {
    const {
      views,
      structures,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && structures && structures.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={structures}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          componentName={ComponentName.STRUCT}
          openNewTab={true}
          onRowClick={this.openEditor}
          columns={this.getColumns()}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.STRUCT} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.STRUCT} />
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };
  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openStructure } = this.props;
    const name = e.data.name;
    openStructure(name);
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.struct.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[25%]',
        copyable: true,
        onCopy: (e: DataStructure) => {
          return e.name;
        },
        body: (record: DataStructure) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={45} text={record.name} data-testid={`${ComponentName.STRUCT}_name_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.struct.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[25%]',
        body: (record: DataStructure) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper
                clipSize={45}
                text={
                  record.description ||
                  LanguageUtil.get({ id: 'pages.struct.table.noDescription', defaultMessage: 'This Struct does not have a description yet.' })
                }
                data-testid={`${ComponentName.STRUCT}_description_text_${record.name}`}
              />
              {/*//record.description || "This Struct does not have a description yet." */}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.struct.table.createdAt', defaultMessage: 'CreatedAt' }),
        className: 'min-w-[20%]',
        body: (record: DataStructure) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.struct.table.updatedAt', defaultMessage: 'UpdatedAt' }),
        className: 'min-w-[20%]',
        body: (record: DataStructure) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.struct.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[60px] 2xl:max-w-[100px]',
        sortable: false,
        body: (record: DataStructure) => {
          const isLocked = ComponentUtil.isLocked(ComponentName.STRUCT, record.name);
          if (isLocked) {
            return null;
          }
          return (
            <div className={'flex w-full justify-center items-center gap-x-4'}>
              <div
                onClick={(e: any) => {
                  e.stopPropagation();
                  e.preventDefault();
                  customConfirm({
                    target: e.currentTarget,
                    accept: async () => await this.structService.remove(record.name),
                    message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
                  });
                }}
                data-testid={`${ComponentName.STRUCT}_remove_button_${record.name}`}
              >
                <Tooltip position={'left'} target={'.remove'}>
                  {LanguageUtil.get({ id: 'pages.struct.table.remove', defaultMessage: 'Remove' })}
                </Tooltip>
                <Icon
                  icon={'delete'}
                  size={20}
                  color={'primary'}
                  className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
                />
              </div>
            </div>
          );
        },
      },
    ];
    return columns.filter((column) => {
      if (column.field === 'action') {
        return RoleHelper.canEdit('Struct');
      }
      return true;
    });
  };
}

interface Props extends ReduxProps {
  structures: DataStructure[];
  openStructure: (id: string) => void;
  views: any;
  currentModal: any;
  onSortChange: (order: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(StructListViewerComponent);
